<template>
  <BaseModal position="right" close-on-outside-click @close="close">
    <div class="container">
      <div class="header">
        <div class="header-left">
          <AiIcon class="header-left__icon" />
          <h2 class="header-left__title">{{ $t('ai.AI_SUGGESTIONS') }}</h2>
        </div>
        <ButtonClose size="small" @click="close" />
      </div>

      <div class="content">
        <AiEntry
          :title="mainEntryTitle"
          :content="currentText"
          :has-ai-text="hasAiText"
          :disabled="isOptionsActive"
          is-main-entry
          @onUseSuggestion="useSuggestion"
        />

        <AiOptions
          v-if="showOptions"
          :can-shorten="canShorten"
          :can-translate="canTranslate"
          @onOpenTranslation="openTranslation"
          @onOpenShorten="openShorten"
        />

        <AiTranslation
          v-if="isTranslationOpen"
          :locale-translate-to="selectedLocale"
          :locales="localesForTranslation"
          :field-name="fieldName"
          @onCancel="closeTranslation"
          @onApprove="approve"
        />

        <AiShorten
          v-if="isShortenOpen"
          :locale="selectedLocale"
          :text="currentText"
          :field-name="fieldName"
          @onCancel="closeShorten"
          @onApprove="approve"
        />
      </div>

      <div class="footer">
        <BaseChip size="tiny" class="header-left__beta-chip">BETA</BaseChip>
        <div class="footer__disclaimer">
          {{ $t('ai.DISCLAIMER') }}
        </div>
      </div>
    </div>
  </BaseModal>
</template>

<script>
import BaseModal from '@/components/generics/base/BaseModal.vue'
import AiIcon from '@/components/icon_components/AiIcon.vue'
import {
  getCorrectLocaleName,
  getListOfLocalesWithContent,
} from '@/helpers/localeHelper'
import { useCompanyLocale } from '@/hooks/useCompanyLocale'
import BaseChip from '../base/BaseChip.vue'
import ButtonClose from '../ButtonClose.vue'
import AiEntry from './AiEntry'
import AiOptions from './AiOptions'
import AiShorten from './AiShorten.vue'
import AiTranslation from './AiTranslation.vue'

export default {
  components: {
    AiEntry,
    AiIcon,
    AiOptions,
    AiShorten,
    AiTranslation,
    BaseChip,
    BaseModal,
    ButtonClose,
  },

  props: {
    localizedContent: {
      type: Object,
      required: true,
    },

    selectedLocale: {
      type: Object,
      required: true,
    },

    fieldName: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: ['onSubmitText', 'onClose'],
  setup() {
    const { contentLocaleOptions } = useCompanyLocale()
    return { contentLocaleOptions }
  },
  data() {
    return {
      isTranslationOpen: false,
      isShortenOpen: false,
      aiText: undefined,
    }
  },

  computed: {
    hasAiText() {
      return !!this.aiText
    },

    mainEntryTitle() {
      const translatedLocaleName = getCorrectLocaleName(this.selectedLocale)
      return `${translatedLocaleName} ${this.fieldName}`
    },

    selectedLocaleText() {
      return this.localizedContent[this.selectedLocale.code]
    },

    currentText() {
      return this.hasAiText ? this.aiText : this.selectedLocaleText
    },

    localesForTranslation() {
      const localesWithContent = getListOfLocalesWithContent(
        this.localizedContent,
        this.contentLocaleOptions
      )
      return localesWithContent.filter(
        (locale) => locale.code !== this.selectedLocale.code
      )
    },

    canTranslate() {
      return this.localesForTranslation.length > 0
    },

    canShorten() {
      return this.currentText?.length > 0
    },

    isOptionsActive() {
      return this.isTranslationOpen || this.isShortenOpen
    },

    showOptions() {
      return !this.isOptionsActive
    },
  },

  methods: {
    openTranslation() {
      this.isTranslationOpen = true
    },

    closeTranslation() {
      this.isTranslationOpen = false
    },

    openShorten() {
      this.isShortenOpen = true
    },

    closeShorten() {
      this.isShortenOpen = false
    },

    approve(content) {
      this.closeTranslation()
      this.closeShorten()
      this.aiText = content
    },

    useSuggestion() {
      this.$emit('onSubmitText', this.currentText)
    },

    close() {
      this.$emit('onClose')
    },
  },
}
</script>

<style scoped lang="scss">
.container {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: $spacing-s;
  width: 520px;
  height: 100%;

  @media screen and (max-width: $tablet-width-max) {
    width: 100%;
    height: 90vh;
  }
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: $spacing-s;
}

.header-left {
  display: flex;
  align-items: center;
  gap: $spacing-5xs;

  &__icon {
    color: $color-purple-50;
  }

  &__title {
    @include subtitle;
  }

  &__beta-chip {
    margin-left: $spacing-2xs;
  }
}

.content {
  display: flex;
  flex-direction: column;
  gap: $spacing-4xs;
  flex-grow: 1;
}

.footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: $spacing-s;
  margin-top: $spacing-3xs;

  &__disclaimer {
    font-size: $font-size-xs;
    color: $color-text-light;
  }
}
</style>
